import type { CSSProperties, FC, KeyboardEvent, ReactNode } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { niceScale } from '@/components/charts/chart-utils'

interface ColumnChartProps {
    title: string
    labels: Array<string>
    values: Array<number>
    unit: string
    height?: number
    className?: string
}

/**
 * Thin vertical columns on a hairline grid, each labelled at its top. Columns grow from the
 * baseline when the parent block reveals. Hover or arrow keys read a single year.
 */
export const ColumnChart: FC<ColumnChartProps> = ({ title, labels, values, unit, height = 240, className }): ReactNode => {
    const [active, setActive] = useState<number | null>(null)
    const scale = niceScale(values, 4)
    const max = scale.max || 1
    const total = values.reduce((sum, value) => sum + value, 0)
    const shown = active ?? values.length - 1

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

        event.preventDefault()
        setActive(current => {
            const base = current ?? values.length - 1

            return Math.min(values.length - 1, Math.max(0, base + (event.key === 'ArrowRight' ? 1 : -1)))
        })
    }

    return (
        <div className={cn('w-full', className)}>
            <p className='hud mb-4 flex justify-between text-mute' aria-live='polite'>
                <span>
                    {labels[shown]}
                </span>

                <span className='text-ink'>
                    {values[shown]} {unit}
                </span>
            </p>

            <div
                tabIndex={0}
                role='group'
                aria-label={`${title}. Use the left and right arrow keys to read each year.`}
                onKeyDown={onKeyDown}
                onBlur={() => setActive(null)}
                onPointerLeave={() => setActive(null)}
                className='relative'
                style={{ height }}
            >
                {scale.ticks.map(tick => (
                    <div key={tick} className='absolute inset-x-0 flex items-center gap-3' style={{ bottom: `${(tick / max) * 100}%` }} aria-hidden='true'>
                        <span className='w-6 -translate-y-1/2 text-right font-mono text-[10px] text-mute'>
                            {tick}
                        </span>

                        <span className={cn('h-px flex-1', tick === 0 ? 'bg-[#3A362F]' : 'bg-hairline')} />
                    </div>
                ))}

                <div className='absolute inset-y-0 left-9 right-0 flex items-end gap-2 sm:gap-4'>
                    {values.map((value, index) => (
                        <div
                            key={labels[index]}
                            onPointerEnter={() => setActive(index)}
                            className='relative flex h-full flex-1 flex-col items-center justify-end'
                        >
                            <span
                                className={cn('font-mono text-[10px] transition-colors duration-300', index === shown ? 'text-ink' : 'text-mute')}
                                aria-hidden='true'
                            >
                                {value}
                            </span>

                            <span
                                className={cn('column-grow mt-1 w-full max-w-[28px] transition-colors duration-300', index === shown ? 'bg-signal' : 'bg-point/80')}
                                style={{ height: `${(value / max) * 100}%`, '--delay': `${index * 70}ms` } as CSSProperties}
                                aria-hidden='true'
                            />
                        </div>
                    ))}
                </div>
            </div>

            <div className='ml-9 mt-3 flex gap-2 sm:gap-4' aria-hidden='true'>
                {labels.map((label, index) => (
                    <span key={label} className={cn('flex-1 text-center font-mono text-[10px] tracking-[0.06em]', index % 2 === 1 ? 'hidden text-mute sm:block' : 'text-mute')}>
                        {label}
                    </span>
                ))}
            </div>

            <table className='sr-only'>
                <caption>
                    {title}. {total} in total.
                </caption>

                <thead>
                    <tr>
                        <th scope='col'>
                            Year
                        </th>

                        <th scope='col'>
                            {unit}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {labels.map((label, index) => (
                        <tr key={label}>
                            <th scope='row'>
                                {label}
                            </th>

                            <td>
                                {values[index]}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
